﻿import { Property } from "csstype";
import mapboxgl, { Layer, LngLatLike, Map, MapboxGeoJSONFeature, MapMouseEvent, Point, RequestParameters, ResourceType, Style } from "mapbox-gl";
import { IReactionDisposer, reaction, toJS } from "mobx";

import { Collection, MapBrowserEvent } from "ol";
import { Coordinate } from "ol/coordinate";
import BaseLayer from "ol/layer/Base";
import LayerGroup from "ol/layer/Group";
import { Options as LayerOptions } from 'ol/layer/Layer';
import { fromLonLat } from "ol/proj";

import MapBoxInteractions, { MapBoxInteractionsOptions } from "../interactions/MapBoxInteractions";
import GeoJsonSpaceProperties from "../models/GeoJsonSpaceProperties";
import { FeatureStates, SpacesInteractionsStore } from "../stores/SpacesInteractionsStore";
import { getExtent } from "../View";

import MapBoxLayer from "./MapBoxLayer";

const emptyStyle: Style = {
    version: 8,
    layers: [],
    sources: {}
}

interface IFeatureState {
    highlighted?: boolean,
    selected?: boolean
}

export interface IFeatureQuery {
    fid: number,
    feature_id?: number,
    top_space_id?: number
}

export interface IFeatureSummary extends IFeatureQuery {
    properties?: GeoJsonSpaceProperties
}

export interface IFeatureResult extends IFeatureSummary {
    secondary?: IFeatureSummary[];
    event?: {
        type: 'map' | 'other',
        coordinate?: Coordinate
    }
}

export interface SpacesLayerOptions {
    container: string | HTMLElement,
    schema?: string,
    access_token: () => string | string,
    center: LngLatLike,
    longitudeExtent?: [number, number],
    multiSelect?: boolean,
    adjustZoom?: number,
    cursor?: Property.Cursor,
}

enum LayerType {
    FrontLayer,
    BackLayer,
    BackLayerSelect,
    BackLayerHighlight,
    ImageLayer
}

class SpacesLayer extends MapBoxLayer {

    public readonly interactions: MapBoxInteractions | undefined;
    public readonly store: SpacesInteractionsStore | undefined;

    public disabled: boolean | { select?: boolean, highlight?: boolean };
    public allowSelectNone: boolean;
    public allowMultiSelect: boolean;
    public allowSelectingByFeatureId: boolean;

    private originalFrontLayerFilters: { [layerId: string]: any[] };
    private disposers: IReactionDisposer[] = [];

    private options: SpacesLayerOptions;
    private stylingLocked: boolean;
    private stylingTimeout: number | undefined;
    private filtersActive: boolean;

    constructor(
        options: SpacesLayerOptions,
        interactionsStore?: SpacesInteractionsStore,
        interactionsOptions?: MapBoxInteractionsOptions,
        layerOptions?: LayerOptions) {

        const { center, container, schema, access_token, longitudeExtent } = options;

        const bounds = getExtent({
            latitide: [-90, 90],
            longitude: longitudeExtent
        })

        super(new Map({
            style: emptyStyle,
            center,
            container,
            attributionControl: false,
            boxZoom: false,
            doubleClickZoom: false,
            dragPan: false,
            dragRotate: false,
            fadeDuration: 0,
            interactive: true,
            keyboard: false,
            pitchWithRotate: false,
            scrollZoom: false,
            touchZoomRotate: false,
            bounds,
            maxBounds: bounds,
            minZoom: -1,
            maxTileCacheSize: 400,
            localIdeographFontFamily: "sans-serif",
            transformRequest: (url: string, resourceType: ResourceType) => {
                const request: RequestParameters = { url };
                if (resourceType === "Tile" || resourceType === "Style" || resourceType === "SpriteImage" || resourceType === "SpriteJSON") {
                    request.headers = {
                        'Authorization': `Bearer ${typeof access_token === 'string' ? access_token : access_token()}`,
                        "X-Application-Schema": schema,
                    }
                }
                return request;
            }
        }), layerOptions, options.adjustZoom);

        this.disabled = false;
        this.allowSelectNone = true;
        this.allowSelectingByFeatureId = true;

        this.stylingLocked = false;
        this.stylingTimeout = undefined;
        this.filtersActive = false;

        this.options = {
            ...options,
            cursor: 'pointer'
        };

        this.allowMultiSelect = this.options.multiSelect ?? false;

        this.store = interactionsStore;
        this.interactions = interactionsStore ? new MapBoxInteractions(this.map, this.getInteractionOptions(interactionsStore, interactionsOptions)) : undefined;

        this.originalFrontLayerFilters = {};

        if (interactionsStore) {
            this.disposers = [
                reaction(() => interactionsStore.selected, (value, previousValue) => {
                    this.select(value, previousValue);
                }),
                reaction(() => interactionsStore.highlighted, (value, previousValue) => {
                    this.highlight(value, previousValue);
                }),
                reaction(() => interactionsStore.filtered, () => {
                    this.filter(interactionsStore.filtered, interactionsStore.customFilter)
                }),
                reaction(() => interactionsStore.customFilter, () => {
                    this.filter(interactionsStore.filtered, interactionsStore.customFilter)
                }),
                reaction(() => interactionsStore.visible, (value) => {
                    this.setVisible(value);
                }),
                reaction(() => interactionsStore.featureStates, (value) => {
                    this.setFeatureState(value);
                })
            ]
        }
    }

    public async setStyle(styleOrUrl: mapboxgl.Style | string | undefined) {
        window.clearTimeout(this.stylingTimeout);

        if (!this.map.isStyleLoaded() || this.stylingLocked) {
            // wait until previous style has fully loaded before applying next
            this.stylingTimeout = window.setTimeout(() => {
                this.setStyle(styleOrUrl);
            }, 500)
        }
        else if (!styleOrUrl) {
            this.stylingLocked = false;
            this.interactions?.setLayers([]);
            this.originalFrontLayerFilters = {};
            this.map.setStyle({ ...emptyStyle });
            this.updateVisuals();
        }
        else {
            // Temp hide layer if filter is active, to avoid screen flickering            
            if (this.filtersActive && this.getVisible()) {
                this.setVisible(false);
            }

            this.interactions?.setLayers([]);
            this.originalFrontLayerFilters = {};

            if (typeof styleOrUrl !== 'string') {

                // save filters of each layer in style, to prevent override while applying client-side adjustments (filter, select,highlight, etc)
                // get filters directly from style object
                const { layers } = styleOrUrl;
                if (layers) {
                    layers.forEach(layer => {
                        const filter = (layer as mapboxgl.Layer)?.filter;
                        if (filter)
                            this.originalFrontLayerFilters[layer.id] = filter;
                    })
                }
            }

            // by locking the styling, we prevent internal styling to occur until external style is loaded
            this.stylingLocked = true;

            // set the external style and wait for it to be loaded
            this.map.setStyle(styleOrUrl, { diff: false })

            // handle loaded style
            this.map.once("styledata", async () => {

                // unlock before getting filters of layers!!
                this.stylingLocked = false;

                const layers = await this.frontLayers();

                if (typeof styleOrUrl === 'string') {
                    // if we could't read the filters from the style directly, we read them after loading style
                    layers.forEach(layer => {
                        const filter = this.map.getFilter(layer.id);
                        this.originalFrontLayerFilters[layer.id] = filter;
                    })
                }

                this.interactions?.setLayers(layers.map(l => l.id));
                this.updateVisuals();
            })
        }
    }

    public async frontLayers() {
        const layers = await this.allLayers();
        return layers.filter(l => this.isFrontLayer(l));
    }

    public async imageLayers() {
        const layers = await this.allLayers();
        return layers.filter(l => this.isImageLayer(l));
    }

    public async backLayers() {
        const layers = await this.allLayers();
        return layers.filter(l => !this.isFrontLayer(l) && !this.isImageLayer(l));
    }

    public async findFeatures(targets: Partial<IFeatureQuery>[]) {
        const layers = await this.frontLayers();
        return this.map.queryRenderedFeatures(undefined, {
            filter: this.getFeaturePartialFilter(targets),
            layers: layers.map(l => l.id)
        })
    }

    public async findFeaturesByProperties(properties: GeoJsonSpaceProperties[], condition: "all" | "any" = "all") {
        var filters: any[] = [];
        properties.forEach(property => {
            Object.keys(property).forEach(key => {
                filters.push(["==", ["get", key], property[key as keyof GeoJsonSpaceProperties]])
            });
        })

        const layers = await this.frontLayers();
        return this.map.queryRenderedFeatures(undefined, {
            filter: filters.length === 1 ? filters[0] : [condition, ...filters],
            layers: layers.map(l => l.id)
        })
    }

    public async featureIsVisible(targets: Partial<IFeatureQuery>[]) {
        return (await this.findFeatures(targets)).length > 0;
    }

    public async getStyle() {
        return new Promise<Style>((resolve, reject) => {
            if (this.map.isStyleLoaded()) {
                resolve(this.map.getStyle());
            }
            else {
                this.map.once("data", () => {
                    resolve(this.getStyle());
                })
            }
        })
    }

    public dispose() {
        this.interactions?.dispose();
        this.disposers.forEach(dispose => dispose());
        super.dispose();
    }

    //#region Private   

    private async allLayers() {
        if (this.stylingLocked)
            return [];
        const style = await this.getStyle();
        return style.layers || [];
    }

    private getInteractionOptions(store: SpacesInteractionsStore, options?: MapBoxInteractionsOptions) {
        options = options ?? {}

        const onClick = options.onClick;
        options.onClick = (features?: MapboxGeoJSONFeature[], e?: MapMouseEvent) => {

            if (!this.isDisabled('select')) {
                const target = this.getFeatureResult(features, e);
                if (target && !this.allowSelectingByFeatureId)
                    target.feature_id = undefined;

                if (target || this.allowSelectNone) {
                    let curSelected = toJS(store.selected);

                    if (this.allowMultiSelect && target && curSelected) {

                        const curIndex = curSelected.findIndex(t => t.fid === target.fid && t.feature_id === target.feature_id);
                        if (curIndex > -1) {
                            curSelected.splice(curIndex, 1);
                        }
                        else
                            curSelected = curSelected.concat([target]);
                    }
                    else if (target) {
                        curSelected = [target];
                    }
                    else
                        curSelected = undefined;

                    store.setSelected(curSelected);
                    if (onClick)
                        onClick(features, e);
                }
            }
        }

        const onHover = options.onHover;
        options.onHover = (features?: MapboxGeoJSONFeature[], e?: MapMouseEvent) => {
            if (!this.isDisabled('highlight')) {
                const target = this.getFeatureResult(features, e);

                const style = this.map.getCanvasContainer().style;
                style.cursor = target ? (this.options.cursor ?? '') : '';

                store.setHighlighted(target ? [target] : undefined);
                if (onHover)
                    onHover(features, e);
            }
        }

        return options;
    }

    private isDisabled(type: 'select' | 'highlight') {
        if (typeof this.disabled === 'boolean')
            return this.disabled;
        else
            return this.disabled[type] === true;
    }

    private updateVisuals() {
        if (this.store) {
            this.filter(this.store.filtered, this.store.customFilter);
            this.select(this.store.selected, []);
            this.highlight(this.store.highlighted, []);
            this.setFeatureState(this.store.featureStates);
            const visible = this.store.visible;
            if (this.filtersActive)
                setTimeout(() => this.setVisible(visible), 50);
            else
                this.setVisible(visible)
        }
    }

    private async filter(targets: Partial<IFeatureQuery>[] | undefined, customFilter: any[] | undefined) {
        const frontLayers = await this.frontLayers();

        if (targets || customFilter?.length) {
            this.filtersActive = true;
            // Hide all front layers
            if (targets && !targets.length) {
                frontLayers.forEach(layer => {
                    //console.log("filter front 1", layer.id, false);
                    this.map.setFilter(layer.id, false);
                });
            }
            // Hide front layers according to targets and/or customFilter
            else {
                const filters: any[] = [];
                if (targets)
                    filters.push(this.getFeaturePartialFilter(targets));

                if (customFilter)
                    filters.push(customFilter.slice());

                frontLayers.forEach(layer => {
                    const orgFilter = this.originalFrontLayerFilters[layer.id];
                    let filter = orgFilter ? [orgFilter, ...filters] : filters;

                    if (filter.length === 1)
                        filter = filter[0];
                    else if (filter.length)
                        filter = ['all', ...filter];
                    else
                        filter = ['boolean', true];

                    //console.log("filter front 2", layer.id, filter);
                    this.map.setFilter(layer.id, filter);
                });
            }
        }
        // Show all front layers 
        else {
            this.filtersActive = false;
            frontLayers.forEach(layer => {
                const filter = this.originalFrontLayerFilters[layer.id] ?? true;
                //console.log("filter front 3", layer.id, filter);
                this.map.setFilter(layer.id, filter);
            });
        }

        await this.setImageLayersFilters(targets, customFilter);
        await this.setBackLayersFilters();
    }

    private async select(targets: IFeatureResult[] | undefined, previousTargets: IFeatureResult[] | undefined) {
        await this.setFeatureStateForTargets(targets, previousTargets, "selected");
    }

    private async highlight(targets: IFeatureResult[] | undefined, previousTargets: IFeatureResult[] | undefined) {
        await this.setFeatureStateForTargets(targets, previousTargets, "highlighted");
    }    

    private async setFeatureStateForTargets(targets: IFeatureResult[] | undefined, previousTargets: IFeatureResult[] | undefined, state: keyof IFeatureState) {

        const layers = await this.allLayers();

        layers.forEach(layer => {
            const isFrontLayer = this.isFrontLayer(layer)

            previousTargets?.forEach(target => {
                this.updateFeatureState(target.fid, layer, { [state]: false });
            })

            targets?.forEach(target => {
                this.updateFeatureState(target.fid, layer, { [state]: true });
                if (isFrontLayer)
                    this.updateSortKey(layer);
            })
        })

        await this.setBackLayersFilters();
    }    

    private async setFeatureState(states: FeatureStates) {
        const layers = await this.allLayers();
        layers.forEach(layer => {
            for (const key in states) {
                const id = parseInt(key);
                this.updateFeatureState(id, layer, states[id]);
            }
        })
    }

    private async setImageLayersFilters(targets: Partial<IFeatureQuery>[] | undefined, customFilter: any[] | undefined) {
        const imageLayers = await this.imageLayers();

        imageLayers.forEach(layer => {
            let isVisible: boolean;

            if (targets?.length && layer.type !== 'custom') {
                const { space_id, feature_id } = layer.metadata;
                isVisible = targets.findIndex(t => t.fid === space_id || t.top_space_id === layer.metadata.top_space_id || t.feature_id === feature_id) !== -1;
            }
            else
                isVisible = !targets;

            this.map.setLayoutProperty(layer.id, "visibility", isVisible ? "visible" : "none");
        });
    }

    private async setBackLayersFilters() {

        if (this.store) {
            const { BackLayer, BackLayerSelect, BackLayerHighlight } = LayerType;
            const { filtered, customFilter } = this.store;
            const backLayers = await this.backLayers();

            for (var layer of backLayers) {
                const type = this.getLayerType(layer);
                let targets: IFeatureQuery[] = [];

                if (this.store.selected?.length && (type === BackLayer || type === BackLayerSelect))
                    targets = targets.concat(this.store.selected);

                if (this.store.highlighted?.length && (type === BackLayer || type === BackLayerHighlight))
                    targets = targets.concat(this.store.highlighted);

                let filter: any[] | boolean;

                if (!targets.length || filtered?.length === 0) {
                    filter = false;
                }
                else {
                    filter = [];
                    if (targets.length)
                        filter.push(this.getFeaturePartialFilter(targets.map(resultToQuery)));

                    if (filtered?.length)
                        filter.push(this.getFeaturePartialFilter(filtered));

                    if (customFilter)
                        filter.push(customFilter.slice());

                    if (filter.length === 1)
                        filter = filter[0];
                    else if (filter.length)
                        filter = ["all", ...filter];
                    else
                        filter = false;
                }

                //console.log("filter back 1", layer.id, filter);
                this.map.setFilter(layer.id, filter);
            }
        }
    }

    private updateSortKey(layer: Layer) {
        const { id, type } = layer;

        if (!this.store || !['fill', 'line', 'symbol', 'circle'].includes(type))
            return;

        const prop = `${type}-sort-key`;
        try {
            if (this.store.selected?.length || this.store.highlighted?.length) {
                const value: any[] = ["case"];

                if (this.store.selected?.length) {
                    value.push(this.getFeatureFilter(this.store.selected.map(resultToQuery), true));
                    value.push(-2);
                }
                if (this.store.highlighted?.length) {
                    value.push(this.getFeatureFilter(this.store.highlighted.map(resultToQuery), true));
                    value.push(-1);
                }
                value.push(0);
                this.map.setLayoutProperty(id, prop, value);
            }
            else
                this.map.setLayoutProperty(id, prop, 0);
        }
        catch {
            //console.error(`No ${prop} property found.`);
        }
    }

    private getFeatureFilter(targets: IFeatureQuery[], includeFeatureId?: boolean) {
        const filters = targets.map(target => {
            const { fid, feature_id } = target;
            const filter = [["==", ["get", "fid"], fid]];
            if (includeFeatureId && feature_id !== undefined)
                filter.push(["==", ["get", "feature_id"], feature_id])

            return filter.length === 1 ? filter[0] : ["all", ...filter];
        });

        return filters.length === 1 ? filters[0] : ["any", ...filters];
    }

    private getFeaturePartialFilter(targets: Partial<IFeatureQuery>[]): any[] {

        const keys: (keyof IFeatureQuery)[] = ['feature_id', 'fid', 'top_space_id'];
        const filters = targets.map(target => {
            const filter: any[] = [];
            for (var key in target) {
                var property = key as (keyof IFeatureQuery);
                if (keys.indexOf(property) !== -1) {
                    var value = target[property];
                    if (value !== undefined)
                        filter.push(["==", ["get", property], value]);
                }
            }
            return filter.length === 1 ? filter[0] : ["all", ...filter];
        })

        return filters.length === 1 ? filters[0] : ["any", ...filters];
    }

    private isFrontLayer(layer: Layer) {
        return this.getLayerType(layer) === LayerType.FrontLayer;
    }

    private isImageLayer(layer: Layer) {
        return this.getLayerType(layer) === LayerType.ImageLayer;
    }

    private getLayerType(layer: Layer) {
        const { id } = layer;
        const { FrontLayer, BackLayer, BackLayerHighlight, BackLayerSelect, ImageLayer } = LayerType;

        if (/-back(:\w*)?$/g.test(id)) {
            if (/:highlight$/.test(id))
                return BackLayerHighlight;
            else if (/:select$/.test(id))
                return BackLayerSelect;
            else
                return BackLayer;
        }
        else if (/-image-tiles-/g.test(id) && layer.type === "raster") {
            return ImageLayer;
        }
        else
            return FrontLayer;
    }

    private getFeatureResult(features?: MapboxGeoJSONFeature[], e?: MapMouseEvent): IFeatureResult | undefined {
        if (features?.length) {
            const mainFeature = this.getFeatureSummary(features[0]);
            if (mainFeature) {
                const coordinate = this.getCoordinateFromMapMouseEvent(e);

                const additionals: IFeatureSummary[] = [];
                features.slice(1).forEach(feature => {
                    const summary = this.getFeatureSummary(feature);
                    if (summary)
                        additionals.push(summary);
                })

                return {
                    ...mainFeature,
                    secondary: additionals.length ? additionals : undefined,
                    event: {
                        coordinate,
                        type: 'map'
                    }
                }
            }
        }
        return undefined;
    }

    private getFeatureSummary(feature: MapboxGeoJSONFeature): IFeatureSummary | undefined {
        const id = parseInt(`${feature.id}`);
        if (!isNaN(id)) {
            const properties = feature.properties as GeoJsonSpaceProperties;
            const { feature_id, top_space_id } = properties;
            return { fid: id, feature_id, top_space_id, properties };
        }
    }

    private getCoordinateFromMapMouseEvent(e?: MapMouseEvent) {
        if (e) {
            const { lat, lng } = e.lngLat;
            return fromLonLat([lng, lat]);
        }
        return undefined;
    }

    /**
     * Note: feature state only works for fid, not for feature_id
     * @param id id in MapboxGeoJSONFeature, or promoted property in mvt style (=fid)
     * @param layer
     * @param state
     */
    private async updateFeatureState(id: number, layer: Layer, state: Record<string, any>) {
        if (typeof layer.source === 'string') {
            const source = layer.source as string;
            const sourceLayer = layer["source-layer"];

            // style needs to be loaded in order for setFeatureState to work
            await this.getStyle();
            this.map.setFeatureState(
                { id, source, sourceLayer }, state
            );
        }
    }

    //#endregion
}

export default SpacesLayer;

export const getFeaturesFromEvent = (evt: MapBrowserEvent<UIEvent>) => {

    const eventFeatures: MapboxGeoJSONFeature[] = [];
    const [x, y] = evt.pixel;
    const point = new Point(x, y);

    const getFeatures = (collection: Collection<BaseLayer>) => {
        const layers = collection.getArray().slice().reverse().sort((a, b) => b.getZIndex() - a.getZIndex());
        for (var layer of layers) {
            if (layer instanceof SpacesLayer && layer.getVisible()) {
                const layerFeatures = layer.interactions?.getFeatures(point);
                if (layerFeatures?.length) {
                    for (var layerFeature of layerFeatures) {
                        const properties = layerFeature.properties as GeoJsonSpaceProperties | undefined;
                        if (properties?.fid !== undefined) {
                            const relatedGeometries = layer.map.queryRenderedFeatures(undefined, {
                                filter: ["==", ["get", 'fid'], properties.fid]
                            }).map(f => f.geometry);

                            if (relatedGeometries) {
                                layerFeature.geometry = {
                                    type: 'GeometryCollection',
                                    geometries: [
                                        layerFeature.geometry,
                                        ...relatedGeometries
                                    ]
                                }
                            }
                        }

                        eventFeatures.push(layerFeature);
                    }
                }
            }
            else if (layer instanceof LayerGroup) {
                getFeatures(layer.getLayers())
            }
        }
    }

    getFeatures(evt.map.getLayers());

    return eventFeatures;
}

const resultToQuery = (result: IFeatureResult): IFeatureQuery => {
    const { fid, feature_id, top_space_id } = result;
    return { fid, feature_id, top_space_id };
}
